import { UseProvidedAuthInterface } from "../../../hooks";
import { Role } from "../../../models";
import {
  EnrollmentDecision,
  OutsideEnrollmentAreaStatus,
  RequestData,
  RequestStatus,
} from "../../../models/Request";
import { Request } from "../../../models/Request";
import { ClaimType } from "../../../models/User";

export const canViewDetailsRequestAdminList = (
  data: RequestData,
  auth: UseProvidedAuthInterface
) => {
  const { claimType, locations, role = 0 } = auth.currentRole ?? {};

  const {
    countyId,
    parentCountyId,
    outsideEnrollmentAreaInstitutionCountyId,
    institutionId,
    countyCommitteeId,
  } = data;

  const isGlobalAccess = claimType === ClaimType.Global;

  const relevantCountyIds = [
    countyId,
    parentCountyId,
    outsideEnrollmentAreaInstitutionCountyId,
  ];
  const isCountyAccess =
    claimType === ClaimType.County &&
    locations?.some((locationId) => relevantCountyIds.includes(locationId));

  const isInstitutionAccess =
    claimType === ClaimType.Institution &&
    locations?.find((v) => v === institutionId) !== undefined;

  const isInstitutionRoleAccess = [
    Role.InstitutionPrincipal,
    Role.InstitutionAdministrator,
  ].includes(role);

  const isRegionalCommitteeAccess =
    role === Role.RegionalCommittee &&
    countyCommitteeId !== undefined &&
    (auth.getCommitteeMembershipByCommitteeId(countyCommitteeId)?.length ?? 0) >
      0;

  return (
    isGlobalAccess ||
    isCountyAccess ||
    isInstitutionAccess ||
    isInstitutionRoleAccess ||
    isRegionalCommitteeAccess
  );
};

export const canViewOutsideEnrollmentAreaDetails = (
  item: RequestData,
  auth: UseProvidedAuthInterface,
  currentSchoolYearId?: number
) => {
  const {
    outsideEnrollmentAreaStatus,
    status,
    schoolYearId,
    outsideEnrollmentAreaInstitutionId,
  } = item;

  const { locations, role = 0 } = auth.currentRole ?? {};

  const isProcessedItem =
    outsideEnrollmentAreaStatus !== OutsideEnrollmentAreaStatus.NotProcessed;

  const isCasOrHelpdeskRole = [Role.CAS, Role.Helpdesk].includes(role);

  const isInstitutionAdminOrPrincipal =
    [Role.InstitutionAdministrator, Role.InstitutionPrincipal].includes(role) &&
    locations?.some((v) => v === outsideEnrollmentAreaInstitutionId);

  const hasValidStatus = [
    RequestStatus.Submitted,
    RequestStatus.OpinionWithRecommendation,
    RequestStatus.EnrollmentApproved,
  ].includes(status);

  const isCurrentSchoolYear = schoolYearId === currentSchoolYearId;

  const canCreateItem =
    (isCasOrHelpdeskRole || isInstitutionAdminOrPrincipal) &&
    hasValidStatus &&
    isCurrentSchoolYear;

  return isProcessedItem || canCreateItem;
};

export const canSaveOutsideEnrollmentAreaRequest = (
  item: Request,
  auth: UseProvidedAuthInterface,
  currentSchoolYearId?: number,
  enrollmentDecision?: EnrollmentDecision
) => {
  const { role = 0, locations, claimType } = auth.currentRole ?? {};
  const {
    countyCommitteeId,
    outsideEnrollmentAreaStatus = OutsideEnrollmentAreaStatus.NotProcessed,
    schoolYearId,
    outsideEnrollmentAreaInstitutionProgram,
    statusId,
  } = item;

  const isCasOrHelpdeskRole = [Role.CAS, Role.Helpdesk].includes(role);

  const isInstitutionAdminOrPrincipal =
    [Role.InstitutionAdministrator, Role.InstitutionPrincipal].includes(role) &&
    locations?.some(
      (v) => v === outsideEnrollmentAreaInstitutionProgram?.institutionId
    ) &&
    outsideEnrollmentAreaStatus !== null &&
    outsideEnrollmentAreaStatus <=
      OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected;

  const isCountyAdminOrLeader =
    [Role.CountyAdministrator, Role.CountyLeader].includes(role) &&
    locations?.some(
      (v) => v === (outsideEnrollmentAreaInstitutionProgram?.parentCountyId ?? outsideEnrollmentAreaInstitutionProgram?.countyId)
    ) &&
    outsideEnrollmentAreaStatus !== null &&
    [
      OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted,
      OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected,
      OutsideEnrollmentAreaStatus.Decision,
    ].includes(outsideEnrollmentAreaStatus);

  const isCountyCommitteePresident =
    (role === Role.CountyCommittee || claimType === ClaimType.Committee) &&
    auth
      .getCommitteeMembershipByCommitteeId(countyCommitteeId)
      ?.some((c) => c.isPresident);

  const hasNoEnrollmentDecisionSolution = !enrollmentDecision?.solutionId;

  const isCurrentSchoolYear = schoolYearId === currentSchoolYearId;

  const hasValidStatus = [
    RequestStatus.Submitted,
    RequestStatus.OpinionWithRecommendation,
    RequestStatus.EnrollmentApproved,
  ].includes(statusId);

  return (
    (isCasOrHelpdeskRole ||
      isInstitutionAdminOrPrincipal ||
      isCountyAdminOrLeader ||
      isCountyCommitteePresident) &&
    hasNoEnrollmentDecisionSolution &&
    isCurrentSchoolYear &&
    hasValidStatus
  );
};
